import { useEffect, useState, useContext, useRef  } from "react";
import toast from "react-hot-toast";
import PaymentTable from "./components/PaymentsList";
import { getAllClients } from "api/clients";
import { ClientContext } from "ClientProvider";
import { getSingleRole } from "api/graphQL_role";
import { getAllGatewaysSecond } from "api/graphQL_gateways";
import { getCardType, getPaymentsFilter } from "api/graphQL_payments";
import { getId } from "utils/auth";

export type PaymentRowObj = {
  id: any;
  payment_id: string;
  et_id: string;
  reference_id:string;
  amount: string;
  currency: string;
  gateway_id: string;
  gateway_name: string;
  status: string;
  meta_info: any;
  payment_type: string;
  client: string;
  refund_info: string;
  created_at: string;
  image: string;
  all_data: any[];
  expanded: boolean;
  payment_history: any;
  gateway_response: any;
  card_type: any;
  customer_name: any;
};

const Payments = () => {
  const [payments, setPayments] = useState<PaymentRowObj[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>();
  const [isPaymentLoading, setIsPaymentLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(0);
  const [totalpage, setTotalPage] = useState<number>(0);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(50);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [clients, setClients] = useState();
  const [value, setValue] = useState(false);
  const [curretClient, setCurrentClient] = useState<string>("all");
  const [allGateways, setAllGateways] = useState();
  const [allCardType, setCardType] = useState();
  const [searchTexts, setSearchTexts] = useState<string>("");
  const [localFilters, setLocalFilters] = useState<any>({});
  const [clearClicked, setClearClick] = useState(0);
  const { paymentType, singleRoleData, setSingleRoleData, setFilter, filter, setLoadingTransaction } = useContext(ClientContext);
  const [loadingState, setLoadingState] = useState({ clients: true, gateways: true, cardTypes: true });

  useEffect(() => { setFilter({}); }, []);

  const fetchClients = () => {
    setIsLoading(true);
    getAllClients(0)
      .then((data) => setClients(data))
      .catch((err) => toast.error(err?.response?.data?.message ?? "Something went wrong while fetching clients"))
      .finally(() => { setIsLoading(false); setLoadingState((p) => ({ ...p, clients: false })); });
  };

  const fetchAllGateways = () => {
    getAllGatewaysSecond()
      .then((data) => setAllGateways(data))
      .catch(() => {})
      .finally(() => setLoadingState((p) => ({ ...p, gateways: false })));
  };

  const fetchCardType = () => {
    getCardType()
      .then((data) => setCardType(data))
      .catch(() => {})
      .finally(() => setLoadingState((p) => ({ ...p, cardTypes: false })));
  };

  useEffect(() => { fetchClients(); fetchAllGateways(); fetchCardType(); }, []);

  const serchboxValueChange = async (searchTerm: string) => {
    const search_txt = searchTerm || "";
    if (search_txt !== searchTexts && searchTexts !== "") {
      await setSearchTexts(search_txt); setPage(1);
    } else {
      await setSearchTexts(search_txt);
    }
  };

  const latestRef = useRef({ page, pageSize, filter, searchTexts });
  latestRef.current = { page, pageSize, filter, searchTexts };
  const requestIdRef = useRef(0);

  const filterData = async (override?: any) => {

    const reqId = ++requestIdRef.current;
    const latest = latestRef.current;
    const hasOverride = override && typeof override === "object" && !("nativeEvent" in override);
    const f = hasOverride ? override : latest.filter;
    const p = hasOverride ? 1 : (latest.page || 1);

    setIsPaymentLoading(true);
    setPayments([]);
    setLoadingTransaction(true);

    try {
      const data = await getPaymentsFilter(p, latest.pageSize, f?.is_dcc, f?.status, f?.client, f?.gateway, f?.card_type, f?.payment_status, f?.timezone, f?.dates?.[0], latest.searchTexts, f?.dates?.[1]);
      if (reqId !== requestIdRef.current) return;

      setPayments(
        Object.entries(data.payments ?? {}).flatMap(
          ([key, value]: any) =>
            value.map((transaction: any) => ({
              ...transaction,
              payment_id: key,
              expanded: false,
              all_data: []
            }))
        )
      );
      setTotalPage(data.total_pages);
      setTotalItems(data.total_items);
      setCurrentPage(data.current_page);
    } catch {
      if (reqId === requestIdRef.current) toast.error("Failed to fetch payments");
    } finally {
      if (reqId === requestIdRef.current) {
        setIsPaymentLoading(false);
        setLoadingTransaction(false);
      }
    }
  };

  useEffect(() => { if (page !== 0) filterData(); }, [page, curretClient, pageSize, value, clearClicked]);
  useEffect(() => { setPage(1); setPageSize(50); setValue((v) => !v); }, [paymentType, filter?.status, searchTexts]);

  const onClearClick = () => setClearClick((prev) => prev + 1);
  const isAllDataReady = Object.values(loadingState).every((v) => v === false);

  return (
    <div className="txn-root"> 
      <div className="org-page-header">
        <div className="org-page-title-block">
          <div className="org-page-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
              <polyline points="17 6 23 6 23 12"/>
            </svg>
          </div>
          <div>
            <h1 className="org-page-heading">Transactions</h1>
            <p className="org-page-sub">
              Manage your Transactions
            </p>
          </div>
        </div>
      </div>
      <PaymentTable
        pageSize={pageSize} setPageSize={setPageSize}
        tableData={payments} fetchPayments={filterData}
        isLoading={isPaymentLoading} page={page} setPage={setPage}
        totalpage={totalpage} totalItems={totalItems} currentPage={currentPage}
        clients={clients} allGateways={allGateways} allCardType={allCardType}
        curretClient={curretClient} setCurrentClient={setCurrentClient}
        roleData={singleRoleData} filterData={filterData}
        filter={filter} setFilter={setFilter}
        setClearClick={onClearClick} onValueChange={serchboxValueChange}
        setLocalFilters={setLocalFilters} localFilters={localFilters}
        isAllDataReady={isAllDataReady} searchTexts={searchTexts}
      />
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        .txn-root {
          font-family: 'Plus Jakarta Sans', sans-serif;
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        .org-page-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 16px;
        }
        .org-page-title-block { display: flex; align-items: center; gap: 14px; }
        .org-page-icon {
          width: 44px; height: 44px; border-radius: 12px;
          background: linear-gradient(135deg, #eff6ff, #dbeafe);
          border: 1.5px solid #bfdbfe;
          display: flex; align-items: center; justify-content: center;
          color: #2563eb; flex-shrink: 0;
        }
        .org-page-heading {
          font-size: 20px; font-weight: 800; color: #0f172a;
          letter-spacing: -0.4px; margin-bottom: 2px; line-height: 22px;
        }
        .org-page-sub { font-size: 13px; color: #94a3b8; font-weight: 500; }
        .org-page-actions { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }

        @media (max-width: 600px) {
          .org-root { padding: 14px; gap: 14px; }
          .org-page-header { flex-direction: column; align-items: flex-start; }
        }
      `}</style>
    </div>
  );
};

export default Payments;